.card {
	position: relative;
	display: block;
	padding: var(--spacing-3);
	overflow: hidden;
	color: inherit;
	text-decoration: none;
	background: var(--colour-background-surface);
	border: 1px solid var(--colour-border-subtle);
	border-radius: var(--radius-lg);
	transition: border-color var(--motion-fast) var(--ease-standard);

	/* Elevation is the 1px border; hover warms it to brand, like the board card. */
	&:hover {
		border-color: var(--colour-brand-primary);
	}

	/* Picked up: the source stays in its slot as a faded, dashed skeleton so you can
	   see where the card will drop — the lifted clone is the DragOverlay (.preview). */
	&.dragging {
		cursor: grabbing;
		border-color: var(--colour-border-strong);
		border-style: dashed;
		opacity: 0.4;
	}

	/* The floating clone under the cursor in the DragOverlay. */
	&.preview {
		cursor: grabbing;
		background: var(--colour-background-raised);
		border-color: var(--colour-border-strong);
		box-shadow: var(--shadow-lg);
		rotate: 2deg;
	}

	/* The colour rail down the leading edge — the epic's own colour. */
	& .rail {
		position: absolute;
		inset-block: 0;
		inset-inline-start: 0;
		inline-size: 3px;
		background: var(--epic-colour);
	}

	/* Top row: the EP-n reference, with the assignee stack + "…" menu pushed to the trailing edge. */
	& .top {
		display: flex;
		gap: var(--spacing-2);
		align-items: center;
		min-block-size: 1.25rem;
		margin-block-end: var(--spacing-2);
	}

	/* The trailing cluster: the assignee stack and (on the live card) the "…" overflow menu. */
	& .topRight {
		display: flex;
		gap: var(--spacing-1);
		align-items: center;
		margin-inline-start: auto;
	}

	/* The distinct task-assignees, overlapping trailing (mirrors the epic detail header). */
	& .assignees {
		display: flex;
		align-items: center;
	}

	/* The "…" overflow menu's stop-propagation wrapper — keeps the icon aligned in the row. */
	& .menu {
		display: inline-flex;
		align-items: center;
	}

	& .avatar {
		display: inline-flex;
		margin-inline-start: -0.375rem;
		border-radius: var(--radius-full);

		/* Ring in the card's own surface colour, so overlapping avatars read as separate. */
		box-shadow: 0 0 0 2px var(--colour-background-surface);

		&:first-child {
			margin-inline-start: 0;
		}
	}

	/* The +N chip closing an overflowing stack — sized + shaped like an xs avatar. */
	& .overflow {
		align-items: center;
		justify-content: center;
		inline-size: 1.5rem;
		block-size: 1.5rem;
		font-size: 0.625rem;
		font-weight: var(--font-weight-semibold);
		font-variant-numeric: tabular-nums;
		color: var(--colour-text-secondary);
		background: var(--colour-background-muted);
	}

	& .key {
		font-family: var(--font-family-mono);
		font-size: var(--font-size-xs);
		font-weight: var(--font-weight-semibold);
		font-variant-numeric: tabular-nums;
		color: var(--colour-text-tertiary);
		letter-spacing: var(--letter-spacing-wide);
	}

	& .name {
		display: flex;
		gap: var(--spacing-2);
		align-items: center;
		margin: 0;
		font-size: var(--font-size-sm);
		font-weight: var(--font-weight-semibold);
		line-height: var(--line-height-snug);
		color: var(--colour-text-primary);
	}

	& .dot {
		flex: none;
		inline-size: 0.5625rem;
		block-size: 0.5625rem;
		background: var(--epic-colour);
		border-radius: var(--radius-full);
	}

	& .nameText {
		min-inline-size: 0;
	}

	/* A muted "Archived" tag next to the name — archived epics surface here when "Show
	   archived" is on, so mark them as cleared from the active set (TAS-73). Kept small
	   and quiet so it reads as a marker, not a second heading (TAS-79). */
	& .archived {
		flex: none;
		padding: 0.0625rem var(--spacing-1);
		font-size: 0.625rem;
		font-weight: var(--font-weight-semibold);
		color: var(--colour-text-tertiary);
		text-transform: uppercase;
		letter-spacing: var(--letter-spacing-wide);
		background: var(--colour-background-muted);
		border-radius: var(--radius-full);
	}

	& .description {
		margin: var(--spacing-2) 0 var(--spacing-3);
		font-size: var(--font-size-xs);
		color: var(--colour-text-secondary);
	}

	& .count {
		display: flex;
		gap: var(--spacing-2);
		justify-content: space-between;
		margin-block-end: var(--spacing-1-5);
		font-size: var(--font-size-xs);
		font-variant-numeric: tabular-nums;
		color: var(--colour-text-tertiary);

		& b {
			font-weight: var(--font-weight-bold);
			color: var(--colour-text-primary);
		}
	}

	& .progress {
		block-size: 0.375rem;
		overflow: hidden;
		background: var(--colour-background-muted);
		border-radius: var(--radius-full);
	}

	& .progressFill {
		display: block;
		block-size: 100%;
		background: var(--epic-colour);
		border-radius: var(--radius-full);
	}
}
